// Modules
import React, { useCallback, useMemo, useState } from 'react'
import { ErrorResponse } from 'apollo-link-error'

// Applications
import { useApp, useLoginContext } from 'context'
import { useAnalytics, useRecoverHook, useRedirectTo } from 'hooks'
import { StatusMessage, ContactType } from 'typings'
import {
    LoginsTypes,
    OutputErrorCode,
    OutputErrorMsg,
    SESSION_STORAGES_KEYS,
    STORAGE_RECOVER_PASSWORD,
    STORAGE_RECOVER_PASSWORD_PHONE,
    STORAGE_RECOVER_PASSWORD_TOKEN,
    STORAGE_RECOVER_PASSWORD_USERNAME,
} from 'constants/index'

// Components
import { ValidateDestinationToken } from 'components/Presenters/ValidateDestinationToken'
import { ValidateTokenInitial } from 'components/Presenters/ValidateToken'
import { defaultConfig } from 'modules/RemoteConfig/defaultConfig'
import { LoginType, User } from 'typings/auth'
import { useUserService } from 'services/user'
import { useRemoteConfig } from 'modules/RemoteConfig/hooks/useRemoteConfig'
import { useLoginService } from 'services/useLoginService'
import { clearStorage } from 'utils'
import { UNICO_CONSTANTS } from 'redesign/constants'

type UserType = 'FULL' | 'LIMITED_ACCESS' | 'UNDERAGE' | undefined

export const ValidateTokenWrapper: React.VFC = () => {
    const recover = useRecoverHook()
    const { openToast } = useApp()
    const { redirectToPath } = useRedirectTo()
    const { trackEvent } = useAnalytics()
    const { operations } = useLoginContext()
    const [isLoading, setLoading] = useState<boolean>(false)
    const [isDisableButton, setIsDisableButton] = useState(false)
    const [isLoadingButton, setIsLoadingButton] = useState(false)
    const [fieldState, setFieldState] = useState<StatusMessage>({ status: '', message: '' })
    const isDestinationMobile = useMemo(() => (recover.contact?.contactType || recover.contact?.type) === ContactType.MOBILE, [
        recover.contact,
    ])
    const { getUserCIPFromBase, getUserFromBase } = useUserService()
    const { isFeatureEnabled } = useRemoteConfig()
    const { updatePatientConfig, ...service } = useLoginService()

    const redirectTo = sessionStorage.getItem('@LiviaAuth:Campaigns')

    function getLoginUserProfile(user: User, userType: UserType): string {
        if (user.tokenType === 'FAP') {
            return LoginsTypes.DATA.LOGIN.USER_TYPE.FAP_ORPHAN
        }
        if (user.limitedFlow || userType === 'LIMITED_ACCESS') {
            return LoginsTypes.DATA.LOGIN.USER_TYPE.CIP_ORPHAN
        }
        if (user.isInvalid) {
            return LoginsTypes.DATA.LOGIN.USER_TYPE.INVALID_CIP_MDM
        }
        if (user.underage || userType === 'UNDERAGE') {
            return LoginsTypes.DATA.LOGIN.USER_TYPE.UNDERAGE
        }
        if (user.loginNav || userType === 'FULL') {
            return LoginsTypes.DATA.LOGIN.USER_TYPE.COMPLETE
        }
        return LoginsTypes.DATA.LOGIN.USER_TYPE.INCOMPLETE
    }

    const reportsHistory = () => {
        if (isFeatureEnabled('AUTH_LOAD_REPORTS_HISTORY')) {
            service.reportsHistory()
        }
    }

    const checkUserType = (user: User): UserType => {
        if (user.underage) {
            return 'UNDERAGE'
        }
        if (user.limitedFlow) {
            return 'LIMITED_ACCESS'
        }

        return 'FULL'
    }

    const loadUser = useCallback(async (): Promise<{ userType: UserType | undefined; user: User | undefined }> => {
        const lsAuthData = operations.getAuthData()
        let userType: UserType | undefined
        let userResponse

        if (lsAuthData?.token) {
            if (lsAuthData?.limitedFlow && !lsAuthData?.refreshToken) {
                userResponse = await getUserCIPFromBase()
            } else {
                userResponse = await getUserFromBase()
                reportsHistory()
            }
            operations.addPayloadLS(lsAuthData, lsAuthData?.limitedFlow || userResponse.underage || userResponse.limitedFlow)

            userType = checkUserType(userResponse)
            operations.setData({
                token: lsAuthData.token,
                refreshToken: lsAuthData.refreshToken,
                signed: !!lsAuthData.token,
                user: userResponse,
                limitedFlow: lsAuthData.limitedFlow || userType !== 'FULL',
            })
        }
        return { user: userResponse, userType }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operations, getUserCIPFromBase, getUserFromBase])

    const handleSubmit = async (values: ValidateTokenInitial) => {
        setIsDisableButton(true)
        setIsLoadingButton(true)
        setFieldState({ status: 'loading', message: 'Validando código' })
        const { token: rawToken } = values
        const token = rawToken.join('')
        const { otpKey, username } = recover

        try {
            recover.setSubmitting(true)
            await recover.setValidateToken({ token })
            const { authenticateOtp } = await recover.checkOTPToken(otpKey!, token, username!)
            recover.setSubmitting(false)

            if (authenticateOtp) {
                setFieldState({ status: 'success', message: 'Código validado' })

                operations.addPayloadTokenLS({
                    token: authenticateOtp.access_token,
                    refreshToken: authenticateOtp.refresh_token,
                    limitedFlow: false,
                    redesign: undefined,
                })
                operations.setIdentifier(LoginType.cpf)

                recover.setOtpKey('')
                clearStorage([
                    STORAGE_RECOVER_PASSWORD_USERNAME,
                    STORAGE_RECOVER_PASSWORD_PHONE,
                    STORAGE_RECOVER_PASSWORD_TOKEN,
                    STORAGE_RECOVER_PASSWORD,
                    UNICO_CONSTANTS.STORAGE,
                    SESSION_STORAGES_KEYS.REDIRECT_TO_URL,
                ])
                recover.resetRecoverPasswordState()

                const { user, userType } = await loadUser()

                trackEvent({
                    event: 'login_success',
                    funnel: {
                        name: 'login_faseado',
                        current_step: 'Login OTP',
                    },
                    user: {
                        id: user?.id,
                        access: 'OTP',
                        profile: getLoginUserProfile(user!, userType),
                    },
                })

                setTimeout(() => {
                    redirectToPath({
                        defaultRedirect: redirectTo ?? defaultConfig.strings.AUTH_REDIRECT_LOGIN,
                    })
                }, 1200)
                return
            }

            if (!authenticateOtp) {
                setFieldState({
                    status: 'error',
                    message: OutputErrorMsg.INVALIDTOKEN,
                })
            }
        } catch (err) {
            setIsLoadingButton(false)
            setFieldState({ status: 'error', message: OutputErrorMsg.INVALIDTOKEN })
            recover.setSubmitting(false)
            const e: ErrorResponse = err
            if (e.graphQLErrors) {
                const codeError = e.graphQLErrors[0].extensions?.code
                if (
                    codeError === OutputErrorCode.INVALIDTOKEN ||
                    codeError === OutputErrorCode.EXPIREDTOKEN ||
                    codeError === OutputErrorCode.FORBIDDEN
                ) {
                    setFieldState({
                        status: 'error',
                        message:
                            codeError === OutputErrorCode.INVALIDTOKEN || OutputErrorCode.FORBIDDEN
                                ? OutputErrorMsg.INVALIDTOKEN
                                : OutputErrorMsg.EXPIREDTOKEN,
                    })
                    trackEvent({
                        event: 'login_failure',
                        funnel: {
                            name: 'login_faseado',
                            current_step: 'Login OTP',
                        },
                        error: {
                            message:
                                codeError === OutputErrorCode.INVALIDTOKEN || OutputErrorCode.FORBIDDEN
                                    ? OutputErrorMsg.INVALIDTOKEN
                                    : OutputErrorMsg.EXPIREDTOKEN,
                        },
                    })
                    return
                }
            }
            openToast('Ocorreu um erro')
        }
    }

    const handleResendToken = async (contactType: ContactType) => {
        try {
            setLoading(true)

            const generateOtpInput = {
                cpf: recover.username,
                type: contactType,
                id: recover.contact?.id,
            }

            const { generateKeyOtp } = await recover.sendOTPToken(generateOtpInput)

            trackEvent({
                event: 'funnel_step',
                funnel: {
                    name: 'login_faseado',
                    current_step: 'Token OTP (reenviar)',
                },
                item_text: `Receber por ${contactType}`,
            })

            recover.setSubmitting(false)
            recover.setOtpKey(generateKeyOtp.key)
        } catch (error) {
            openToast('Ocorreu um erro')
        } finally {
            setLoading(false)
        }
    }

    return (
        <ValidateDestinationToken
            fieldState={fieldState}
            handleResendToken={handleResendToken}
            handleSubmit={handleSubmit}
            isDestinationMobile={isDestinationMobile}
            isDisableButton={isDisableButton}
            isLoading={isLoading}
            isLoadingButton={isLoadingButton}
            isOtp={true}
            showHelpModal={false}
        />
    )
}
